import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { X, CheckCircle2, AlertCircle, Info, XCircle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const noop = () => undefined;
const fallbackToastContext: ToastContextType = {
  showToast: noop,
  showSuccess: noop,
  showError: noop,
  showInfo: noop,
  showWarning: noop,
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    const mode = (import.meta as any)?.env?.MODE;
    if (mode !== 'test' && mode !== 'production') {
      console.warn('useToast called outside ToastProvider. Returning no-op handlers.');
    }
    return fallbackToastContext;
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', duration: number = 5000) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: Toast = { id, message, type, duration };

      setToasts((prev) => [...prev, newToast]);

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [removeToast]
  );

  const showSuccess = useCallback(
    (message: string, duration?: number) => {
      showToast(message, 'success', duration);
    },
    [showToast]
  );

  const showError = useCallback(
    (message: string, duration?: number) => {
      showToast(message, 'error', duration);
    },
    [showToast]
  );

  const showInfo = useCallback(
    (message: string, duration?: number) => {
      showToast(message, 'info', duration);
    },
    [showToast]
  );

  const showWarning = useCallback(
    (message: string, duration?: number) => {
      showToast(message, 'warning', duration);
    },
    [showToast]
  );

  const getToastConfig = (type: ToastType) => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircle2,
          bgColor: '#d1fae5',
          borderColor: '#10b981',
          textColor: '#065f46',
          iconColor: '#10b981',
        };
      case 'error':
        return {
          icon: XCircle,
          bgColor: '#fee2e2',
          borderColor: '#ef4444',
          textColor: '#991b1b',
          iconColor: '#ef4444',
        };
      case 'warning':
        return {
          icon: AlertCircle,
          bgColor: '#fef3c7',
          borderColor: '#f59e0b',
          textColor: '#92400e',
          iconColor: '#f59e0b',
        };
      case 'info':
      default:
        return {
          icon: Info,
          bgColor: '#dbeafe',
          borderColor: '#3b82f6',
          textColor: '#1e40af',
          iconColor: '#3b82f6',
        };
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showInfo, showWarning }}>
      {children}
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          maxWidth: '400px',
          width: '100%',
          pointerEvents: 'none',
        }}
        className="toast-container"
      >
        {toasts.map((toast) => {
          const config = getToastConfig(toast.type);
          const Icon = config.icon;

          return (
            <div
              key={toast.id}
              style={{
                backgroundColor: config.bgColor,
                border: `1px solid ${config.borderColor}`,
                color: config.textColor,
                padding: '12px 16px',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                pointerEvents: 'auto',
                animation: 'slideInRight 0.3s ease-out',
                minWidth: '300px',
              }}
              className={`toast toast-${toast.type}`}
              role="alert"
              aria-live="polite"
            >
              <Icon size={20} style={{ color: config.iconColor, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '14px', fontWeight: 500 }}>{toast.message}</span>
              <button
                onClick={() => removeToast(toast.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: config.textColor,
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: 0.7,
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.7';
                }}
                aria-label="Fechar notificação"
              >
                <X size={16} />
              </button>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @media (max-width: 768px) {
          .toast-container {
            top: 10px;
            right: 10px;
            left: 10px;
            max-width: 100%;
          }
          
          .toast {
            min-width: auto !important;
          }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

